import { createRaceTimer } from './primitives/raceTimer'
import { createWarpPadHandler } from './primitives/warpPad'
import { BehaviorHandler, MissionBehaviors, HudStyle } from './types'

/**
 * RJF07（M7）のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJF07.rbxl）のギミックを移植する:
 * - Start（スタートゲート、透明な板）+ StarterGui/LocalScript:
 *   接触で StartEvent → タイマー開始（-1 始まりで毎秒 +1、最初の表示は 0）。
 *   再接触してもタイマー進行中は何もしない（isStart ガード）。同時に
 *   ServerStorage.Event でゴールの touched フラグをリセットする（再走可能に）。
 * - Goal（ゴールのピンクのブロック）GoalScript: 接触でタイマー停止
 *   （GoalEvent → isGoal）。接触のたびに WalkSpeed=32 / JumpPower=100。
 * - ワープ元/WarpScript: 接触で同じ ID 属性のワープ先 + (0,5,0) へテレポート。
 *   プレビューのシーンはペアが 1 組のため名前（ワープ先）で解決する。
 *
 * プレビューでの近似・意図的な差異:
 * - タイマー表示は ScreenGui/TextLabel の代わりに HUD テキスト
 *   （BehaviorContext.setHudText）で再現する。
 * - BGM（Funky Escapade - Tag5）と ゴール音（Spinning Around (b)）は
 *   アセット取得不可のため、ゴール時のみ WebAudio の短い上昇ジングルで
 *   近似し、BGM は省略する。
 *
 * タイマー・ワープの実装は rjf08 と同型のため primitives/ を構成する
 * （世代トークンの設計判断は primitives/raceTimer.ts を参照）。
 */

/** ゴール接触時の歩行速度（Lua: humanoid.WalkSpeed = 32）。 */
const GOAL_WALK_SPEED = 32

/** ゴール接触時のジャンプ力（Lua: humanoid.JumpPower = 100）。 */
const GOAL_JUMP_POWER = 100

/** スタートゲートのオブジェクト名。 */
const START_NAME = 'Start'

/** ゴール（ピンクのブロック）のオブジェクト名。 */
const GOAL_NAME = 'Goal'

/** ワープ元/ワープ先のオブジェクト名。 */
const WARP_FROM_NAME = 'ワープ元'
const WARP_TO_NAME = 'ワープ先'

/** タイマー GUI の実機デザイン（ScreenGui/TextLabel の実測値）。 */
const TIMER_HUD_STYLE: HudStyle = {
  background: '#FFFF7F',
  color: '#000000',
  border: '#FF0000',
  borderWidth: 3,
  left: 5,
}

export const createRjf07Behaviors = (): MissionBehaviors => {
  // タイマー状態はプレイ毎に factory から作り直されるため、プレイ内でだけ生きる。
  const timer = createRaceTimer({
    hudStyle: TIMER_HUD_STYLE,
    // Lua: GoalScript は接触のたびに速度・ジャンプ力を引き上げる。
    applyGoalEffect: (world) => {
      world.setAvatarWalkSpeed(GOAL_WALK_SPEED)
      world.setAvatarJumpPower(GOAL_JUMP_POWER)
    },
  })

  const onTouchCharacter = new Map<string, BehaviorHandler>()
  onTouchCharacter.set(START_NAME, timer.onStartTouched)
  onTouchCharacter.set(GOAL_NAME, timer.onGoalTouched)
  onTouchCharacter.set(WARP_FROM_NAME, createWarpPadHandler(WARP_TO_NAME))

  return { onPlay: [timer.showInitialTimer], onTouchCharacter }
}
